import { Inline, Text, textVariants, vars } from '@etg/wings';
import { Icon, Overlay } from '@eti/components';
import { CaretRight } from '@phosphor-icons/react';
import { useDirection, useSnowplowTracking, useTranslation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { AgeType } from '@eti/schema-types';
import { useState } from 'react';
import { constants } from '@eti/utils';
import ClickableCard from '../../../../../common/components/ClickableCard';
import type { ExtraProductType, TravelerType } from '../../../models';
import type { BoundBaggageInfoType } from '../baggages/models';
import PassengerDrawer from './PassengerDrawer';

const flexItemStyles = css`
  flex: 1;
`;

const inlineStyles = css`
  justify-content: space-between;
`;

interface PassengerCardProps {
  baggagePerBound: BoundBaggageInfoType[];
  meal?: ExtraProductType | null;
  passenger: TravelerType;
  passengerIndex: number;
  seat?: ExtraProductType | null;
}

const PassengerCard = ({
  baggagePerBound,
  meal,
  passenger,
  passengerIndex,
  seat,
}: PassengerCardProps) => {
  const { isRTL } = useDirection();
  const { t } = useTranslation();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const [isOpen, setIsOpen] = useState(false);

  const handleSnowplowOnPassengerCard = () => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Overview',
        action: 'Click',
        label: 'PASSENGER_CARD',
      });
    }
  };

  const iconIdPerAgeType = {
    [AgeType.Inf]: 'infant-traveler-outlined',
    [AgeType.Cnn]: 'child-traveler-outlined',
    [AgeType.Adt]: 'adult-traveler-outlined',
  };

  const labelPerAgeType = {
    [AgeType.Inf]: t('Infant'),
    [AgeType.Cnn]: t('Child'),
    [AgeType.Adt]: t('Adult'),
  };

  return (
    <>
      <ClickableCard
        aria-controls={`passenger-card-${passengerIndex}`}
        aria-expanded={isOpen ? 'true' : 'false'}
        aria-haspopup="dialog"
        data-testid={`passenger-card-${passengerIndex}`}
        onCardSelection={() => {
          setIsOpen(true);
          handleSnowplowOnPassengerCard();
        }}
        padding={16}
      >
        <Inline alignY="center" className={inlineStyles} noWrap>
          <Inline alignY="center" className={flexItemStyles} noWrap spacing={8}>
            <Icon
              aria-label={labelPerAgeType[passenger.ageType]}
              color={vars.colors.decoration.dark}
              data-testid={`${iconIdPerAgeType[passenger.ageType]}-${passenger.id}-icon`}
              id={iconIdPerAgeType[passenger.ageType]}
              isGraphic
              size="20px"
            />
            <Text
              className={cx(flexItemStyles, constants.MOUSE_FLOW_CLASS_NAMES)}
              variant={textVariants._16_BOLD}
            >
              {passenger.name}
            </Text>
          </Inline>
          <CaretRight aria-hidden="true" color="#262626" mirrored={isRTL} size={24} />
        </Inline>
      </ClickableCard>
      <Overlay.Drawer
        dataTestId="passenger-information-drawer"
        handleClose={() => setIsOpen(false)}
        id={`passenger-card-${passengerIndex}`}
        isOpen={isOpen}
      >
        <Overlay.Header
          onClose={() => setIsOpen(false)}
          shouldApplyMouseflowMask
          title={passenger.name}
        />
        <Overlay.Content data-testid="passenger-information-drawer-content">
          {passenger.id && (
            <PassengerDrawer
              baggagePerBound={baggagePerBound}
              meal={meal}
              passenger={passenger}
              seat={seat}
            />
          )}
        </Overlay.Content>
      </Overlay.Drawer>
    </>
  );
};

export default PassengerCard;
